import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Form, FormInstance, Input, Tooltip } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import { debounce as _debounce } from 'lodash';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import SpinnerWrapper from 'components/spinner/spinner';
import Checkbox from 'components/checkbox/checkbox';
import { FilterBar2Value, FilterContentProps } from '../types';
import { useFocusInput } from '../../../custom-hooks';
import { Translations } from '../filter-bar-2';
import { filterOption } from '../utils';

import styles from './filter-content-checkbox.module.scss';

export interface FilterContentCheckboxProps extends FilterContentProps {
	translations?: Translations;
	selectAllTooltip?: string;
	selectAllTooltipText?: string;
	isVisible?: boolean;
	allowSelectAll?: boolean;
	disableSelectAll?: boolean;
	selectAllThreshold?: number;
	selectAllTooltipThreshold?: number;
	form: FormInstance;
	onSearch?: (value: string, name: NamePath) => Promise<any>;
}
export const FilterContentCheckbox = ({
	name,
	values,
	onFilterChange,
	form,
	isVisible,
	allowSelectAll,
	disableSelectAll = false,
	selectAllThreshold,
	onSearch,
	selectAllTooltip,
	selectAllTooltipThreshold,
	translations = {
		search: 'Search',
		selectAll: 'Select All'
	} as Translations
}: FilterContentCheckboxProps) => {
	const [searchValue, setSearchValue] = useState('');
	const [loading, setLoading] = useState(false);
	const watchedValues = Form.useWatch(name, form);
	const selectedValues = useMemo(() => watchedValues || [], [watchedValues]);
	const inputRef = useRef(null);
	const selectedValuesRef = useRef(selectedValues);
	const selectedValueLabelsRef = useRef<Map<string | number, string>>(new Map());

	const listValues = values;
	const [currentSearchResults, setCurrentSearchResults] = useState<FilterBar2Value[]>([]);

	useEffect(() => {
		selectedValuesRef.current = selectedValues;
	}, [selectedValues]);

	useEffect(() => {
		setSearchValue('');
		setCurrentSearchResults([]);
		setLoading(false);
	}, [isVisible]);

	const preserveSelectedValuesLabels = useCallback((result: FilterBar2Value[]) => {
		result.forEach(item => {
			selectedValueLabelsRef.current.set(item.value, item.label);
		});
	}, []);

	const getDefaultDisplayValues = (): FilterBar2Value[] => {
		const hasOnSearch = !!onSearch;
		let allListValues: FilterBar2Value[] = [];
		if (hasOnSearch) {
			allListValues = (watchedValues || [])
				.map(value => listValues.find(item => item.value === value))
				.filter(Boolean);
		} else {
			allListValues = Array.isArray(listValues) ? listValues : [];
		}
		const listValuesMap = new Map(allListValues.map(item => [`${item.value}`, item]));

		allListValues.forEach(item => {
			selectedValueLabelsRef.current.set(item.value, item.label);
		});

		const isPredefinedValuesList = !selectedValues || selectedValues.length === 0 || !onSearch;

		if (isPredefinedValuesList) {
			return [...allListValues];
		} else {
			return selectedValues.map(value => {
				const valueStr = `${value}`;
				const listItem = listValuesMap.get(valueStr);
				if (listItem) {
					return listItem;
				}
				const storedLabel = selectedValueLabelsRef.current.get(value);
				return {
					value,
					label: storedLabel || `${value}`
				};
			});
		}
	};

	const filteredValues = useMemo((): FilterBar2Value[] => {
		let result: FilterBar2Value[] = [];

		const isSearchingOnPredefinedList = searchValue && !onSearch;
		const isAsyncSearch = !!onSearch;

		if (isAsyncSearch && searchValue.trim().length >= 2) {
			result = currentSearchResults;
		} else if (isSearchingOnPredefinedList) {
			result = (listValues as FilterBar2Value[])?.filter(item => filterOption(searchValue, item)) || [];
		} else {
			result = getDefaultDisplayValues();
		}
		return result;
	}, [searchValue, listValues, onSearch, currentSearchResults, selectedValues]);

	const handleDebouncedSearch = _debounce(async (value: string) => {
		const trimmedValue = value.trim();

		if (trimmedValue.length < 2) {
			setLoading(false);
			return;
		}

		setLoading(true);
		try {
			const lookupListValues = await onSearch(trimmedValue, name);
			const searchResults = [
				...new Map(lookupListValues?.map(item => [`${item.value}`, item])).values()
			] as FilterBar2Value[];

			preserveSelectedValuesLabels(searchResults);

			setCurrentSearchResults(searchResults);
		} finally {
			setLoading(false);
		}
	}, 500);

	const onSearchCallback = useMemo(() => {
		if (!onSearch) {
			return (value: string) => {
				setSearchValue(value);
			};
		}

		return (value: string) => {
			setSearchValue(value);
			return handleDebouncedSearch(value);
		};
	}, [onSearch, handleDebouncedSearch]);

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		onSearchCallback(e.target.value);
	};

	const clearSearch = () => {
		setSearchValue('');
		setLoading(false);
		onSearchCallback('');
	};

	useFocusInput({
		inputRef,
		isVisible
	});

	const selectedFilteredValues = selectedValues.filter(value => filteredValues.some(item => item.value === value));
	const isAllSelected = selectedFilteredValues.length === filteredValues.length;
	const isIndeterminate = selectedFilteredValues.length > 0 && selectedFilteredValues.length < filteredValues.length;

	const handleCheckboxGroupChange = useCallback(
		(checkedValues: any[]) => {
			const currentFormValues = selectedValuesRef.current || [];
			const currentDisplayValues = filteredValues.map(item => item.value);
			const hiddenSelectedValues = currentFormValues.filter(val => !currentDisplayValues.includes(val));

			const finalValues = [...new Set([...hiddenSelectedValues, ...checkedValues])];

			selectedValuesRef.current = finalValues;
			form.setFieldsValue({ [name]: finalValues });

			onFilterChange();
		},
		[filteredValues, form, name, onFilterChange]
	);

	const handleSelectAll = useCallback(() => {
		if (isAllSelected) {
			const currentDisplayValues = filteredValues.map(item => item.value);
			const remainingValues = selectedValues.filter(value => !currentDisplayValues.includes(value));
			form.setFieldsValue({ [name]: remainingValues });
		} else {
			const currentDisplayValues = filteredValues.map(item => item.value);
			const existingValues = selectedValues || [];
			const mergedValues = [...new Set([...existingValues, ...currentDisplayValues])];
			form.setFieldsValue({ [name]: mergedValues });
		}
		onFilterChange();
	}, [filteredValues, form, name, onFilterChange, isAllSelected, selectedValues]);

	return (
		<div className={styles.filterContentCheckbox}>
			<Input
				data-test-id={`filter-content-checkbox-search-${name}`}
				ref={inputRef}
				value={searchValue}
				onChange={handleSearch}
				placeholder={translations.search}
				prefix={<BringgIcon iconName={BringgFontIcons.Search} />}
				suffix={
					searchValue ? (
						<span onClick={clearSearch} style={{ cursor: 'pointer' }}>
							<BringgIcon iconName={BringgFontIcons.Close} />
						</span>
					) : null
				}
				className={styles.searchInput}
			/>

			<SpinnerWrapper spinning={loading} size="small">
				{allowSelectAll && filteredValues?.length >= selectAllThreshold && !!searchValue && (
					<>
						<div className={styles.selectAll}>
							<Tooltip
								title={filteredValues.length >= selectAllTooltipThreshold ? selectAllTooltip : ''}
								mouseEnterDelay={0.2}
							>
								<Checkbox
									disabled={disableSelectAll}
									checked={isAllSelected}
									indeterminate={isIndeterminate}
									onChange={handleSelectAll}
									data-test-id={`filter-content-checkbox-select-all-${name}`}
								>
									<div className={styles.selectAllText}>
										{translations.selectAll} ({filteredValues.length})
									</div>
								</Checkbox>
							</Tooltip>
						</div>
						<div className={styles.divider} />
					</>
				)}

				<Form.Item
					name={name}
					className={classNames(styles.resultsContainer, { [styles.bordered]: !!allowSelectAll })}
				>
					<Checkbox.Group onChange={handleCheckboxGroupChange}>
						{filteredValues.map(item => (
							<div className={classNames(styles.item, 'filter-bar-2-base-row')} key={item.value}>
								<Checkbox
									value={item.value}
									data-test-id={`filter-content-checkbox-item-${item.value}`}
								>
									{item.label}
								</Checkbox>
							</div>
						))}
					</Checkbox.Group>
				</Form.Item>
			</SpinnerWrapper>
		</div>
	);
};
